/**
 * /pilot/methodology — "How the numbers are computed" white-paper.
 *
 * Pure static long-form content, originally ported from
 * claude/ahmed/methodology.html via a dangerouslySetInnerHTML body.
 * Composed with the Oatmeal taupe + Mona Sans theme:
 * Container/Heading/Subheading/Text/Document elements over the
 * taupe-100 ground.
 *
 * F11 of the Oatmeal rebuild verified the Oatmeal element vocabulary
 * is in place (this file landed in the React-engine port era already
 * using them). The OS dark-mode hook is mounted alongside the document-
 * title hook so OS-dark visitors get the dark Oatmeal palette here
 * too (same behavior as /pilot under F20).
 *
 * Ungated, like the original standalone page.
 */

import React from 'react';
import {Link} from 'react-router-dom';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {useOsDarkMode} from '../hooks/useOsDarkMode';
import {Container} from '../oatmeal/elements/container';
import {Heading} from '../oatmeal/elements/heading';
import {Subheading} from '../oatmeal/elements/subheading';
import {Text} from '../oatmeal/elements/text';
import {Eyebrow} from '../oatmeal/elements/eyebrow';
import {Document} from '../oatmeal/elements/document';
import {ArrowNarrowLeftIcon} from '../oatmeal/icons/arrow-narrow-left-icon';
import '../styles/oatmeal.css';

// Long-form prose styling is provided by Oatmeal's Document element — the
// only piece it doesn't ship with is a table style, so we define one here
// scoped to the methodology page.
const methTableClass =
  'w-full border-collapse text-sm/6 text-taupe-700 dark:text-taupe-400 ' +
  '[&_th]:border-b [&_th]:border-taupe-300 dark:[&_th]:border-taupe-800 [&_th]:py-2 [&_th]:pr-4 [&_th]:text-left [&_th]:font-medium [&_th]:text-taupe-950 dark:[&_th]:text-white ' +
  '[&_td]:border-b [&_td]:border-taupe-200 dark:[&_td]:border-taupe-900 [&_td]:py-2 [&_td]:pr-4 [&_td]:align-top';

function MethTable({children}: {children: React.ReactNode}) {
  return (
    <div className="not-prose my-6 overflow-x-auto">
      <table className={methTableClass}>{children}</table>
    </div>
  );
}

function MethSection({
  id,
  number,
  title,
  children,
}: {
  id: string;
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="flex flex-col gap-6 scroll-mt-24">
      <Subheading className="text-3xl/10 sm:text-4xl/12">
        <span className="mr-3 text-taupe-400 dark:text-taupe-600">{number}</span>
        {title}
      </Subheading>
      <Document>{children}</Document>
    </section>
  );
}

export default function MethodologyPage() {
  useDocumentTitle('Planapus — How the simulation works');
  useOsDarkMode();

  return (
    <div className="oatmeal-root min-h-screen">
      <header className="border-b border-taupe-200 dark:border-taupe-800">
        <Container className="flex items-center justify-between py-5">
          <Link
            to="/pilot"
            className="inline-flex items-center gap-2 text-sm/7 font-medium text-taupe-700 hover:text-taupe-950 dark:text-taupe-400 dark:hover:text-white"
          >
            <ArrowNarrowLeftIcon className="size-4" />
            Back to Planapus
          </Link>
          <span className="text-xs/5 font-medium text-taupe-500 uppercase tracking-wide">
            Methodology · Last updated 2026‑05‑12
          </span>
        </Container>
      </header>

      <main className="py-16 sm:py-24">
        <Container className="flex flex-col gap-12 sm:gap-16">
          <div className="flex max-w-3xl flex-col gap-6">
            <Eyebrow>Methodology</Eyebrow>
            <Heading className="max-w-3xl text-balance">
              How the numbers are computed
            </Heading>
            <Text size="lg" className="flex flex-col gap-4">
              <p>
                The numbers you see in the Planapus sandbox — the side-by-side
                races, the distribution of 10,000 runs, the hours and dollars
                reclaimed — are not estimates pulled from a slide. They are
                produced by a simulation that builds an imaginary version of
                your organization, walks one of your processes through it
                10,000 times, and shows you what changes when the work is
                routed by an AI orchestrator instead of by inbox.
              </p>
              <p>
                This page explains the assumptions behind that simulation, the
                parameters you can dial, and the bounds we put on how good
                "with Planapus" is allowed to look.
              </p>
            </Text>
          </div>

          <nav className="max-w-2xl rounded-2xl border border-taupe-200 bg-taupe-50 p-6 dark:border-taupe-800 dark:bg-taupe-900">
            <p className="text-xs/5 font-semibold uppercase tracking-wide text-taupe-500">
              Contents
            </p>
            <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm/7 text-taupe-700 marker:text-taupe-400 dark:text-taupe-300">
              <li><a href="#what-it-models" className="hover:text-taupe-950 dark:hover:text-white">What the simulation models</a></li>
              <li><a href="#world" className="hover:text-taupe-950 dark:hover:text-white">Each run is a small invented world</a></li>
              <li><a href="#people" className="hover:text-taupe-950 dark:hover:text-white">The people in that world</a></li>
              <li><a href="#calendar" className="hover:text-taupe-950 dark:hover:text-white">Time, calendars, and the working day</a></li>
              <li><a href="#routing" className="hover:text-taupe-950 dark:hover:text-white">The one thing Planapus changes: routing</a></li>
              <li><a href="#per-step" className="hover:text-taupe-950 dark:hover:text-white">What happens at each handoff</a></li>
              <li><a href="#ai" className="hover:text-taupe-950 dark:hover:text-white">When AI does the work itself</a></li>
              <li><a href="#bounds" className="hover:text-taupe-950 dark:hover:text-white">The floor and the ceiling</a></li>
              <li><a href="#knobs" className="hover:text-taupe-950 dark:hover:text-white">The dials in "Your numbers"</a></li>
              <li><a href="#failure" className="hover:text-taupe-950 dark:hover:text-white">When a process dies mid-run</a></li>
              <li><a href="#aggregation" className="hover:text-taupe-950 dark:hover:text-white">How 10,000 runs become one chart</a></li>
              <li><a href="#fair" className="hover:text-taupe-950 dark:hover:text-white">Why the comparison is fair</a></li>
              <li><a href="#caveats" className="hover:text-taupe-950 dark:hover:text-white">What this simulator does not model</a></li>
            </ol>
          </nav>

          <div className="flex flex-col gap-16">
            <MethSection id="what-it-models" number={1} title="What the simulation models">
              <p>
                Every process you can pick in the sandbox — vendor onboarding,
                a hiring loop, a permit application, an insurance submission —
                is described as an ordered sequence of <strong>handoffs</strong>.
                Person A finishes something and passes it to Person B; B
                reviews, acts, passes it onward. In the real world, these
                handoffs are the source of most of the cycle time you live
                with: not the work itself, but the waiting in between.
              </p>
              <p>
                The simulator's job is to be honest about that waiting. It
                does not assume work is instantaneous, that everyone is at
                their desk, or that the next person in line is always
                available. It models the coordination tax that the pitch
                points at, and then asks: what happens to that tax when an
                orchestration layer sits on top?
              </p>
            </MethSection>

            <MethSection id="world" number={2} title="Each run is a small invented world">
              <p>
                A single Monte Carlo run starts by inventing an entire
                micro-organisation: a staffed roster for each function in the
                process (Legal, Procurement, Finance, Security, and so on),
                with each named person having their own calendar, working
                hours, time zone, queue depth, and propensity for errors.
                None of these are typed in by hand — they are drawn from
                statistical distributions tuned to feel realistic.
              </p>
              <p>
                Once that world exists, the same process is walked through it
                twice: once the way work flows today (the originally-assigned
                person handles it, even if they're out of office for a week),
                and once the way work would flow with a coordination layer on
                top (whoever in the team is actually available picks it up).
                The world doesn't change between those two walks. The only
                thing that changes is routing.
              </p>
              <p>
                That deliberate equality is what makes the head-to-head fair.
                We're not comparing "an average bad day" against "an average
                good day." We're showing the <em>same</em> Tuesday — Bob is
                still on the same vacation, Eva is still the same kind of
                careful, the same draft is still owed back to the same lawyer
                — and asking what routing alone changes.
              </p>
            </MethSection>

            <MethSection id="people" number={3} title="The people in that world">
              <p>
                Every function in the process is staffed by between one and
                eight specific people. Smaller companies have fewer per
                function, larger ones more. A startup might have a single HR
                person; a mid-market company has four. The size of the
                rosters scales with the org-size you picked at the top of the
                sandbox.
              </p>
              <MethTable>
                <thead>
                  <tr>
                    <th>Company size</th>
                    <th>Internal team</th>
                    <th>Customer-side team</th>
                    <th>External party</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Startup (under 50 employees)</td><td>1</td><td>1</td><td>1</td></tr>
                  <tr><td>SMB (50–500)</td><td>2</td><td>1</td><td>1</td></tr>
                  <tr><td>Mid-market (500–5,000)</td><td>4</td><td>2</td><td>2</td></tr>
                  <tr><td>Enterprise (5,000+)</td><td>8</td><td>3</td><td>2</td></tr>
                </tbody>
              </MethTable>
              <p>
                Each of those people then gets their own personality, drawn
                from a bucket that depends on their function. Lawyers are slow
                and careful. Procurement is fast but error-prone. Government
                counterparts are very slow. These are not slurs — they are
                calibrations based on realistic distributions of response
                time, drop rate, and error rate in each function.
              </p>
              <MethTable>
                <thead>
                  <tr>
                    <th>Function</th>
                    <th>Typical response time (hours)</th>
                    <th>Error rate</th>
                    <th>Drop rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Legal</td><td>18–48</td><td>1–3%</td><td>0.3–1.2%</td></tr>
                  <tr><td>Procurement</td><td>12–36</td><td>3–6%</td><td>1.0–3.0%</td></tr>
                  <tr><td>Finance</td><td>10–30</td><td>2–5%</td><td>0.6–2.0%</td></tr>
                  <tr><td>HR</td><td>6–22</td><td>2–4%</td><td>0.8–2.5%</td></tr>
                  <tr><td>Security</td><td>14–36</td><td>2–4%</td><td>0.5–1.5%</td></tr>
                  <tr><td>Engineering</td><td>6–18</td><td>3–6%</td><td>0.5–2.0%</td></tr>
                  <tr><td>Sales</td><td>4–14</td><td>2–5%</td><td>0.8–3.0%</td></tr>
                  <tr><td>Government / agency</td><td>48–120</td><td>4–8%</td><td>1.0–3.0%</td></tr>
                  <tr><td>External vendor / partner</td><td>24–72</td><td>3–6%</td><td>0.5–2.0%</td></tr>
                </tbody>
              </MethTable>
              <p>
                "Drop rate" is the probability that on any given handoff this
                person simply loses or fumbles the item — the request never
                gets processed, the deal dies in their inbox. "Error rate" is
                the probability they introduce a mistake that requires
                rework: the form gets sent back, the redline misses a clause,
                the wrong invoice is filed.
              </p>
              <p>
                Each person also has a personal load factor (some people are
                just busier than others, which stretches their hands-on
                time), a personal response sigma (how variable their typical
                response time is around the central tendency for their
                function), and their own set of vacation and sick days for
                the simulated window.
              </p>
            </MethSection>

            <MethSection id="calendar" number={4} title="Time, calendars, and the working day">
              <p>
                Every person works a standard 9-to-5 in their own time zone,
                five days a week. The simulator picks each person's time zone
                from somewhere in North America — Eastern through Hawaii —
                and the spread across zones is one of the dials you can tune.
                When the "working-hours overlap" knob is high, everyone is in
                the same zone; when it's low, your team is spread
                coast-to-coast and Hawaii.
              </p>
              <p>
                On top of working hours, each person has zero, one, or two
                vacation blocks of three to seven days within the simulated
                window, plus occasional sick days (around 3% of working
                days). When the work arrives at someone who is out of office,
                today's reality is that the work waits — for days, sometimes
                for a week. That is the single biggest factor in how slow a
                process actually runs.
              </p>
              <p>
                The whole simulation plays out over a 90-day window. Anything
                that doesn't finish within those 90 days is counted as a
                failure — a proxy for "the deal dies of old age" or "we had
                to give up and chase it as an exception."
              </p>
            </MethSection>

            <MethSection id="routing" number={5} title="The one thing Planapus changes: routing">
              <p>
                When a handoff lands at, say, "Customer Legal," the simulator
                has to pick which specific lawyer on that team it goes to.
                There are two ways to do this, and they correspond exactly to
                the two lanes in the sandbox:
              </p>
              <MethTable>
                <thead><tr><th>Lane</th><th>Routing rule</th></tr></thead>
                <tbody>
                  <tr>
                    <td><strong>Today</strong></td>
                    <td>The original assignee. Always the same person on the team. If they're out of office for five days, the work sits in their inbox for five days.</td>
                  </tr>
                  <tr>
                    <td><strong>With Planapus</strong></td>
                    <td>Whoever on the team is actually available soonest. Same team, same people, same calendars — Planapus just knows who is at their desk and routes accordingly.</td>
                  </tr>
                </tbody>
              </MethTable>
              <p>
                That single difference is the whole story on the right-hand
                lane. Planapus doesn't replace the lawyer; it doesn't
                outsource the paperwork; it doesn't bypass any approval. It
                changes one rule: "work waits for one specific person"
                becomes "work flows to whoever is actually available right
                now."
              </p>
            </MethSection>

            <MethSection id="per-step" number={6} title="What happens at each handoff">
              <p>
                For every handoff the simulator goes through, the wall-clock
                time is a sum of three things:
              </p>
              <ul>
                <li><strong>Wait.</strong> How long until the assigned person is actually at their desk to look at this. If they are out of office, this can be days.</li>
                <li><strong>Queue.</strong> Even when they are at their desk, this person has other things in front of yours. Drawn from a distribution centered on their typical response time.</li>
                <li><strong>Touch.</strong> The actual hands-on time the work takes, scaled by how busy this particular person is. If they're loaded, it takes longer.</li>
              </ul>
              <p>
                Sometimes the person makes a mistake (governed by their
                personal error rate). When that happens, we add roughly 70%
                more touch time for the rework and increase the labor cost
                accordingly. Sometimes they drop it entirely (governed by
                their personal drop rate). When that happens, the run fails
                and the process dies — exactly the kind of silent attrition
                that turns a "30-day deal" into a 90-day deal or a lost one.
              </p>
              <p>
                All of this is then stretched across the person's working
                calendar. Twelve hours of work that arrive at 4pm on a Friday
                don't finish at 4am Saturday — they finish on Monday
                afternoon at the earliest, and later if the person is off
                Monday too.
              </p>
            </MethSection>

            <MethSection id="ai" number={7} title="When AI does the work itself">
              <p>
                Some handoffs in a process can be done by an AI agent rather
                than by a human. Common examples: extracting data from a
                form, drafting a templated email, validating routine inputs,
                summarising a long document for review, scheduling a meeting.
                When AI handles a handoff:
              </p>
              <ul>
                <li><strong>The wait disappears.</strong> AI doesn't have a calendar, doesn't go on vacation, doesn't get to it tomorrow. The handoff is picked up within three to fifteen minutes.</li>
                <li><strong>The queue disappears.</strong> AI doesn't have a backlog of other items to triage first.</li>
                <li><strong>A human still reviews.</strong> The AI's output is checked by a person before it moves on. How much time that review takes depends on how consequential the handoff is.</li>
              </ul>
              <p>
                The review tail is what keeps the model honest. A 12-second
                AI draft of a 20-page legal redline still needs a lawyer to
                read it. A 30-second AI prefill of a form field needs barely
                a glance. Different handoffs warrant very different amounts
                of review:
              </p>
              <MethTable>
                <thead><tr><th>Type of handoff</th><th>Typical hands-on review</th></tr></thead>
                <tbody>
                  <tr><td>Sending a routine request, firing a notification, scheduling a meeting</td><td>~10% of original touch time</td></tr>
                  <tr><td>Prefilling a form, validating routine data, status emails</td><td>~20%</td></tr>
                  <tr><td>Drafting a contract section, summarising a document for a reviewer</td><td>~35%</td></tr>
                  <tr><td>Producing a substantive deliverable — long questionnaire, deep extract, structured analysis</td><td>~50%</td></tr>
                </tbody>
              </MethTable>
              <p>
                These review percentages are not assumed to be the same for
                every AI step. Each handoff in the catalog can specify its
                own, based on what the work actually entails. Where it isn't
                specified, we use the table above as a sensible default.
              </p>
              <p>
                Not every handoff is AI-eligible. A notary signing a
                document, a fire inspector visiting a facility, a CEO
                authorising a deal — these require the human, and Planapus
                does not pretend otherwise. On those steps the AI doesn't do
                the work; the orchestration layer just routes it
                intelligently.
              </p>
            </MethSection>

            <MethSection id="bounds" number={8} title="The floor and the ceiling">
              <p>
                Two principled bounds are built into the model to keep it
                from over- or under-claiming.
              </p>
              <h3 className="mt-6 font-display text-xl/8 text-taupe-950 dark:text-white">
                The floor: Planapus is worth something even with zero automation
              </h3>
              <p>
                Even when no step is AI-handled, the simulator credits
                Planapus with making each handoff at least{' '}
                <strong>10% more efficient</strong> on the human side. The
                argument is that an orchestration layer helps the person
                doing the work even when it isn't doing the work: it pre-fills
                context, surfaces the handoff the moment it lands instead of
                waiting for someone to notice an email, removes data
                re-entry, and reduces the cognitive load of keeping track of
                where a long-running process actually stands.
              </p>
              <p>
                Without this floor, a sandbox run with automation turned all
                the way down would suggest Planapus is indistinguishable from
                today — which is the wrong story. Workflow orchestration is
                a real benefit even before any AI takes over the keyboard.
              </p>
              <h3 className="mt-6 font-display text-xl/8 text-taupe-950 dark:text-white">
                The ceiling: every step still has a human review tail
              </h3>
              <p>
                No matter how aggressive the automation settings are, no
                individual handoff can ever be modelled as more than{' '}
                <strong>95% faster</strong> than it is today. A small
                human-review tail is always preserved. This is partly a
                quality argument — software you can't audit shouldn't be
                making decisions you can't see — and partly a realism
                argument: the moment a vendor claims their AI does something
                at 100%, you should ask what it gets wrong.
              </p>
            </MethSection>

            <MethSection id="knobs" number={9} title="The dials in 'Your numbers'">
              <p>
                Every dial in the sandbox's "Your numbers" panel maps onto a
                real lever in the simulation. None of them are cosmetic.
              </p>
              <MethTable>
                <thead><tr><th>Dial</th><th>What it controls</th></tr></thead>
                <tbody>
                  <tr><td>Loaded hourly cost</td><td>Pure dollar multiplier. Move it to match your team's fully-loaded rate (salary + benefits + overhead).</td></tr>
                  <tr><td>Runs per year</td><td>How many times this process runs annually across your business. Multiplies all the per-year aggregates.</td></tr>
                  <tr><td>Average response time</td><td>How long someone in your org typically takes to respond to a handoff. Stretches every person's queue depth proportionally.</td></tr>
                  <tr><td>Working-hours overlap</td><td>How synchronised your team's hours are. High overlap means everyone is in the same zone with overlapping calendars; low means people are spread coast-to-coast and handoffs hit the time-of-day wall.</td></tr>
                  <tr><td>Error / rework rate</td><td>Probability a handoff arrives with mistakes and bounces back. Multiplies each person's drawn error rate.</td></tr>
                  <tr><td>Out-of-office probability</td><td>How often your people are unavailable. Scales the frequency of vacation and sick-day blocks.</td></tr>
                  <tr><td>Ghost / drop-off rate</td><td>Probability a handoff dies entirely on someone's plate (deal lost, candidate ghosted). Multiplies each person's drop rate.</td></tr>
                  <tr><td>Automation potential</td><td>Of all the steps in the process, what share are eligible to be handled by AI in your environment. Picks the highest-value automatable steps first.</td></tr>
                  <tr><td>Planapus coverage</td><td>Of the automation-eligible steps, what share Planapus actually orchestrates today vs. lets humans continue to handle.</td></tr>
                </tbody>
              </MethTable>
            </MethSection>

            <MethSection id="failure" number={10} title="When a process dies mid-run">
              <p>Two things can kill a run:</p>
              <ul>
                <li><strong>A drop.</strong> On some handoff, the assigned person simply fumbles it. The request is lost; the deal is dead; the candidate has stopped returning calls. The process ends right there.</li>
                <li><strong>An overrun.</strong> The process is still in flight when the 90-day simulation window closes. Even if nobody dropped anything, the cycle time has stretched past what most stakeholders will tolerate.</li>
              </ul>
              <p>
                Both count as failed runs and contribute to the "process dies
                mid-run" failure rate shown beneath the histogram. The
                difference between today's failure rate and Planapus's
                failure rate, multiplied by your runs-per-year, is the "deals
                saved" / "permits saved" / "hires saved" number near the
                bottom of the report.
              </p>
            </MethSection>

            <MethSection id="aggregation" number={11} title="How 10,000 runs become one chart">
              <p>
                Each process is simulated 10,000 times against 10,000
                different invented worlds (different vacations, different
                roll-of-the-dice error events, different people). The
                histogram is the resulting distribution of total cycle times
                — how often does this process finish in 5 days, in 12 days,
                in 40 days. The lines marked "median" and "P90" show where
                half the runs and ninety percent of the runs fall,
                respectively.
              </p>
              <p>
                The slider above the side-by-side lanes lets you watch any
                one of those 10,000 runs. Trial #1 has its own world; trial
                #4,231 has a different one. When you scrub, both lanes update
                to show the same trial — same Bob, same Eva, same vacation,
                same dice.
              </p>
              <p>
                The number under the histogram — "in N% of these 10,000 runs,
                Planapus finishes faster" — counts only the runs where
                Planapus was <em>strictly</em> faster than today. Ties (where
                routing converges on the same person and produces the same
                timing on the same dice) are not counted as wins. That number
                reads as "Planapus improves at least N% of cases," and we
                want it to be honest about what it claims.
              </p>
            </MethSection>

            <MethSection id="fair" number={12} title="Why the comparison is fair">
              <p>
                A common worry with simulated comparisons is that the
                better-looking side got lucky dice. We avoid that with a
                technique called <em>common random numbers</em>: every trial
                generates one invented world, and both routing strategies are
                then asked to walk the same process through that exact world.
                The same person is on vacation the same week. The same lawyer
                is the same kind of slow. The coin-flips that govern whether
                someone drops the request are rolled once, and both lanes see
                the result.
              </p>
              <p>
                What ends up different between the two lanes is therefore
                attributable to routing and AI — not to variance. That is the
                technical reason we feel comfortable saying "Planapus is
                faster in 99% of these 10,000 runs" without it being a parlor
                trick.
              </p>
            </MethSection>

            <MethSection id="caveats" number={13} title="What this simulator does not model">
              <p>
                To save you the trouble of asking: a few things are
                deliberately out of scope, either because they would
                over-complicate the model or because they don't yet have
                well-calibrated answers.
              </p>
              <ul>
                <li><strong>Parallel handoffs.</strong> Real processes sometimes fan out — two reviewers in parallel, then re-join. We walk a single linear order.</li>
                <li><strong>Conditional branches.</strong> If the legal redline is rejected, real life goes back to step 3. We don't model that loop.</li>
                <li><strong>Retries after a drop.</strong> When someone drops a handoff today, eventually a manager intervenes. We treat the drop as terminal — a conservative choice.</li>
                <li><strong>AI errors and AI cost.</strong> The simulator's AI never makes a mistake and never costs an inference token. In practice both happen; we'll model them as we get good data on the failure rates of specific automations.</li>
                <li><strong>Cross-process contention.</strong> Each trial simulates one run of one process. We don't model the fact that the same lawyer is also drowning under nine other deals.</li>
              </ul>
              <p>
                These are honest omissions, not glossed-over weaknesses. As
                the product and the data behind it mature, this page will be
                updated to reflect what's in and what's not.
              </p>
            </MethSection>
          </div>

          <p className="border-t border-taupe-200 pt-8 text-sm/7 text-taupe-500 dark:border-taupe-800 dark:text-taupe-400">
            This document is maintained alongside the simulator. Whenever the
            model changes — a prior shifts, a bound is renegotiated, a new
            effect is introduced — this page is updated to match.
          </p>
        </Container>
      </main>
    </div>
  );
}
